import { useState, useEffect } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';

interface PinRecord {
  id: string;
  entity_type: string;
  entity_id: string;
}

interface PinButtonProps {
  entityType: string;
  entityId: string;
  label: string;
}

export function PinButton({ entityType, entityId, label }: PinButtonProps) {
  const [pinId, setPinId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet<PinRecord[]>('/pins')
      .then(pins => {
        const match = pins.find(p => p.entity_type === entityType && p.entity_id === entityId);
        setPinId(match?.id ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  async function toggle() {
    if (pinId) {
      // Unpin
      setPinId(null);
      try {
        await apiDelete(`/pins/${pinId}`);
        window.dispatchEvent(new Event('dossier:pins-changed'));
      } catch {
        // Revert
        setPinId(pinId);
      }
    } else {
      // Pin
      try {
        const pin = await apiPost<PinRecord>('/pins', { entity_type: entityType, entity_id: entityId, label });
        setPinId(pin.id);
        window.dispatchEvent(new Event('dossier:pins-changed'));
      } catch {
        // ignore
      }
    }
  }

  if (loading) return null;

  return (
    <button
      onClick={toggle}
      className={`p-1.5 rounded-md transition-colors ${
        pinId
          ? 'text-accent hover:text-accent-hover hover:bg-accent/10'
          : 'text-text-muted hover:text-text-primary hover:bg-bg-hover'
      }`}
      title={pinId ? 'Unpin from sidebar' : 'Pin to sidebar'}
    >
      {pinId ? <PinOff size={16} /> : <Pin size={16} />}
    </button>
  );
}
